export interface User {
  userID: number,
  userRole: string,
  firstName: string,
  middleName: string | null,
  lastName: string,
  username: string,
  password: string | null,
  enabled: boolean,
  externalIdentities: { [key: string] : string },
}

export interface ReportTeacher {
  userID: number,
  displayName: string,
}

export const UserRoles = {
  FIL_ADMIN: 'ROLE_FIL_ADMIN',
  FIL_ADMIN_VIEW: 'ROLE_FIL_ADMIN_VIEW', // maybe not used anymore?
  PARENT: 'ROLE_PARENT',
  TEACHER: 'ROLE_TEACHER',
  TEACHER_CURRICULUM: 'ROLE_TEACHER_CURRICULUM',
  TEACHER_REVIEW: 'ROLE_TEACHER_REVIEW',
  TEACHER_SCHOOL_REPORTS: 'ROLE_TEACHER_SCHOOL_REPORTS',
  TEACHER_SCHOOL_ADMIN: 'ROLE_TEACHER_SCHOOL_ADMIN',
  SCHOOL_REPORTS: 'ROLE_SCHOOL_REPORTS',
  SCHOOL_ADMIN: 'ROLE_SCHOOL_ADMIN',
  DISTRICT_REPORTS: 'ROLE_DISTRICT_REPORTS',
  CUSTOMER_SERVICE: 'ROLE_CUSTOMER_SERVICE',
  FOUNDATIONS_REPORTS: 'ROLE_FOUNDATIONS_REPORTS',
  SALES_REP: 'ROLE_SALES_REP',
  PARTNER_SUCCESS: 'ROLE_PARTNER_SUCCESS',
} as const ;

export type UserRole = typeof UserRoles[keyof typeof UserRoles] ;

const RANKED_ROLES: UserRole[] = [
  'ROLE_FIL_ADMIN',
  'ROLE_FIL_ADMIN_VIEW',
  'ROLE_FOUNDATIONS_REPORTS',
  'ROLE_PARTNER_SUCCESS',
  'ROLE_SALES_REP',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_DISTRICT_REPORTS',
  'ROLE_SCHOOL_ADMIN',
  'ROLE_TEACHER_SCHOOL_ADMIN',
  'ROLE_SCHOOL_REPORTS',
  'ROLE_TEACHER_SCHOOL_REPORTS',
  'ROLE_TEACHER_CURRICULUM',
  'ROLE_TEACHER',
  'ROLE_TEACHER_REVIEW',
] ;

export const UserRoleGroups = {
  PARENTS: [
    UserRoles.PARENT
  ],
  TEACHERS: [
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW
  ],
  MANAGE_SUBSCRIPTIONS: [
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW,
    UserRoles.SCHOOL_ADMIN,
    UserRoles.CUSTOMER_SERVICE,
  ],
  MANAGE_COTEACHERS: [
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW,
    UserRoles.SCHOOL_ADMIN,
    UserRoles.TEACHER_SCHOOL_ADMIN,
  ],
  TEACHERS_PARENTS: [
    UserRoles.PARENT,
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW
  ],
  STUDENT_ENROLLMENT: [
    UserRoles.PARENT,
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW,
    UserRoles.SCHOOL_ADMIN,
  ],
  FOUNDATIONS_REPORTS: [
    UserRoles.FOUNDATIONS_REPORTS
  ],
  PARTNER_SUCCESS: [
    UserRoles.FOUNDATIONS_REPORTS,
    UserRoles.PARTNER_SUCCESS,
  ],
  NOT_CUSTOMER_SERVICE: [
    UserRoles.PARENT,
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW,
    UserRoles.TEACHER_SCHOOL_ADMIN,
    UserRoles.SCHOOL_REPORTS,
    UserRoles.SCHOOL_ADMIN,
    UserRoles.DISTRICT_REPORTS,
    UserRoles.FOUNDATIONS_REPORTS,
    UserRoles.SALES_REP,
    UserRoles.PARTNER_SUCCESS,
  ],
  NOT_PARENTS_NOT_CUSTOMER_SERVICE: [
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW,
    UserRoles.TEACHER_SCHOOL_ADMIN,
    UserRoles.SCHOOL_REPORTS,
    UserRoles.SCHOOL_ADMIN,
    UserRoles.DISTRICT_REPORTS,
    UserRoles.FOUNDATIONS_REPORTS,
    UserRoles.SALES_REP,
    UserRoles.PARTNER_SUCCESS,
  ],
  DISTRICT_REPORTS: [
    UserRoles.SCHOOL_REPORTS,
    UserRoles.DISTRICT_REPORTS,
    UserRoles.FOUNDATIONS_REPORTS,
    UserRoles.SALES_REP,
    UserRoles.PARTNER_SUCCESS,
    UserRoles.TEACHER_SCHOOL_ADMIN,
    UserRoles.SCHOOL_ADMIN,
  ],
  REPORTS: [
    UserRoles.TEACHER_SCHOOL_REPORTS,
    UserRoles.TEACHER_SCHOOL_ADMIN,
    UserRoles.SCHOOL_REPORTS,
    UserRoles.SCHOOL_ADMIN,
    UserRoles.DISTRICT_REPORTS,
    UserRoles.FOUNDATIONS_REPORTS,
    UserRoles.SALES_REP,
    UserRoles.PARTNER_SUCCESS,
  ],
  RESOURCES: [
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW,
    UserRoles.TEACHER_SCHOOL_ADMIN,
    UserRoles.SCHOOL_REPORTS,
    UserRoles.SCHOOL_ADMIN,
    UserRoles.DISTRICT_REPORTS,
    UserRoles.FOUNDATIONS_REPORTS,
    UserRoles.SALES_REP,
    UserRoles.PARTNER_SUCCESS,
  ],
  CUSTOMERS: [
    UserRoles.PARENT,
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW,
    UserRoles.TEACHER_SCHOOL_ADMIN,
    UserRoles.SCHOOL_REPORTS,
    UserRoles.SCHOOL_ADMIN,
    UserRoles.DISTRICT_REPORTS,
  ],
  FILES: [
    UserRoles.TEACHER,
    UserRoles.TEACHER_SCHOOL_REPORTS,
    UserRoles.TEACHER_SCHOOL_ADMIN,
    UserRoles.DISTRICT_REPORTS,
    UserRoles.SCHOOL_REPORTS,
    UserRoles.SCHOOL_ADMIN,
    UserRoles.FOUNDATIONS_REPORTS,
    UserRoles.CUSTOMER_SERVICE,
    UserRoles.PARTNER_SUCCESS,
  ],
  USER_MANAGEMENT: [
    UserRoles.TEACHER_SCHOOL_ADMIN,
    UserRoles.SCHOOL_ADMIN,
  ],
  NOTIFICATIONS: [
    UserRoles.TEACHER,
    UserRoles.TEACHER_CURRICULUM,
    UserRoles.TEACHER_REVIEW,
    UserRoles.TEACHER_SCHOOL_ADMIN,
  ],
}

export const Genders = {
  FEMALE: 1,
  MALE: 2,
}

export const getAcceptableRoles = (role: string) => {
  const roleIndex = RANKED_ROLES.indexOf(role as UserRole) ;
  if (roleIndex == -1) return [] ;

  return RANKED_ROLES.slice(roleIndex + 1, RANKED_ROLES.length) ;
} ;
